import { ChangeEvent, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { MultiSelectDropdown } from "../../components/dropdown/MultiSelectDropdown";
import Modal from "../../components/modal/Modal";
import SendEmailNotificationToUsers from "./SendEmailNotificationToUsers";

interface Premise {
  id: string;
  premisesName: string;
  responsiblePerson: string;
  floor: string;
  region: string;
  [key: string]: any;
}

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

interface FireDrillFormData {
  _id?: string;
  region: string;
  premise: string;
  premisesId: string;
  report_send_email?: string[];
  date_of_test_drills: string | Date | null;
  person_responsibility: string;
  closing_timeline: Date | string | null;
  date_of_evidence: string | Date | null;
  participation_rate: string | number;
  observation_identified_for_improvement: string;
  area: string;
  floor: string;
  users?: string[];
  actions: string;
  closure_evidence: string;
  average_drill: string | number;
  evacuation_trend: string | number;
  next_drill_scheduled: string | Date | null;
  evidence_closure_files: File[] | string[] | null;
  status?: "scheduled" | "completed";
  feedback_gap_analysis?: string;
}

interface CreateDrillSectionProps {
  formData: FireDrillFormData;
  errors: Record<string, string>;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  premisesList: Premise[];
  regionList: string[];
  listOfUsersFromPremisesId: OrgUser[];
  handleUserSelectChange: (userIds: string[]) => void;
  setFormData: React.Dispatch<React.SetStateAction<FireDrillFormData>>;
  moduleName?: string;
}

export const CreateDrillSection: React.FC<CreateDrillSectionProps> = ({
  formData,
  errors,
  handleChange,
  premisesList,
  regionList,
  listOfUsersFromPremisesId,
  handleUserSelectChange,
  setFormData,
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [creatingListOfFloors, setCreatingListOfFloors] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (!formData.premisesId) {
      setCreatingListOfFloors([]);
      return;
    }

    const selectedPremise = premisesList.find(
      (p) => p.id === formData.premisesId
    );
    if (!selectedPremise?.floor) {
      setCreatingListOfFloors([]);
      return;
    }

    const floorsCount = Number(selectedPremise.floor);
    if (isNaN(floorsCount)) {
      setCreatingListOfFloors([]);
      return;
    }

    const floors = Array.from(
      { length: floorsCount },
      (_, i) => `Floor ${i + 1}`
    );
    setCreatingListOfFloors(floors);
  }, [formData.premisesId, premisesList]);

  useEffect(() => {
    if (formData.users && formData.users.length > 0) {
      const selectedEmails = listOfUsersFromPremisesId
        .filter((user) => {
          const userId =
            typeof user._id === "object" && "$oid" in user._id
              ? user._id.$oid
              : String(user._id);
          return formData.users?.includes(userId);
        })
        .map((user) => user.email);
      setFormData((prev) => ({
        ...prev,
        report_send_email: selectedEmails,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        report_send_email: [],
      }));
    }
  }, [formData.users, listOfUsersFromPremisesId, setFormData]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Region */}
        <div>
          <label className="block mb-1 font-medium">
            Region <span className="text-red-500">*</span>
          </label>
          <select
            name="region"
            value={formData.region}
            onChange={handleChange}
            className={`w-full border-2 ${
              errors.region ? "border-red-500" : "border-gray-400"
            } rounded p-2 focus:outline-none focus:border-blue-500`}
          >
            <option value="">Select</option>
            {regionList.map((region, index) => (
              <option key={index} value={region}>
                {region}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="text-red-500 text-sm mt-1">{errors.region}</p>
          )}
        </div>

        {/* Premise */}
        <div>
          <label className="block mb-1 font-medium">
            Premise <span className="text-red-500">*</span>
          </label>
          <select
            name="premise"
            value={formData.premisesId}
            onChange={handleChange}
            className={`w-full border-2 ${
              errors.premise ? "border-red-500" : "border-gray-400"
            } rounded p-2 focus:outline-none focus:border-blue-500`}
          >
            <option value="">Select</option>
            {premisesList.map((premise) => (
              <option key={premise.id} value={premise.id}>
                {premise.premisesName}
              </option>
            ))}
          </select>
          {errors.premise && (
            <p className="text-red-500 text-sm mt-1">{errors.premise}</p>
          )}
        </div>

        {/* Date of test/drill */}
        <div>
          <label className="block mb-1 font-medium">
            Date of test/drill <span className="text-red-500">*</span>
          </label>
          <div>
            <DatePicker
              selected={
                typeof formData.date_of_test_drills === "string"
                  ? (() => {
                      const [day, month, year] =
                        formData.date_of_test_drills.split("/");
                      const parsed = new Date(`${month}/${day}/${year}`);
                      return isNaN(parsed.getTime()) ? null : parsed;
                    })()
                  : formData.date_of_test_drills
              }
              wrapperClassName="datePicker"
              onChange={(date: string | Date | null) =>
                setFormData({ ...formData, date_of_test_drills: date })
              }
              placeholderText="DD/MM/YYYY"
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              className={`w-full pl-10 border-2 ${
                errors.date_of_test_drills
                  ? "border-red-500"
                  : "border-gray-400"
              } rounded p-2 focus:outline-none focus:border-blue-500`}
            />
          </div>
          {errors.date_of_test_drills && (
            <p className="text-red-500 text-sm mt-1">
              {errors.date_of_test_drills}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 font-medium">
            Responsible person <span className="text-red-500">*</span>
          </label>
          <select
            name="person_responsibility"
            value={formData.person_responsibility}
            onChange={handleChange}
            className={`w-full border-2 ${
              errors.person_responsibility
                ? "border-red-500"
                : "border-gray-400"
            } rounded p-2 focus:outline-none focus:border-blue-500`}
          >
            <option value="">Select Responsible Person</option>
            {listOfUsersFromPremisesId.map((user) => (
              <option
                key={
                  typeof user._id === "object" && "$oid" in user._id
                    ? user._id.$oid
                    : String(user._id)
                }
                value={
                  typeof user._id === "object" && "$oid" in user._id
                    ? user._id.$oid
                    : String(user._id)
                }
              >
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
          {errors.person_responsibility && (
            <p className="text-red-500 text-sm mt-1">
              {errors.person_responsibility}
            </p>
          )}
        </div>

        {/* Timeline to close */}
        <div>
          <label className="block mb-1 font-medium">
            Timeline to close <span className="text-red-500">*</span>
          </label>
          <div className="w-full">
            <DatePicker
              selected={
                typeof formData.closing_timeline === "string"
                  ? (() => {
                      const [hours, minutes] =
                        formData.closing_timeline.split(":");
                      const date = new Date();
                      if (!isNaN(Number(hours)) && !isNaN(Number(minutes))) {
                        date.setHours(Number(hours));
                        date.setMinutes(Number(minutes));
                        date.setSeconds(0);
                        date.setMilliseconds(0);
                        return date;
                      }
                      return null;
                    })()
                  : formData.closing_timeline
              }
              onChange={(time: Date | null) =>
                setFormData({ ...formData, closing_timeline: time })
              }
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              wrapperClassName="datePicker"
              timeCaption="Time"
              dateFormat="hh:mm:aa"
              placeholderText="hh:mm:aa"
              className={`w-full pl-10 border-2 ${
                errors.date_of_test_drills
                  ? "border-red-500"
                  : "border-gray-400"
              } rounded p-2 focus:outline-none focus:border-blue-500`}
            />
          </div>
          {errors.closing_timeline && (
            <p className="text-red-500 text-sm mt-1">
              {errors.closing_timeline}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 font-medium">
            Area <span className="text-red-500">*</span>
          </label>
          <input
            name="area"
            value={formData.area}
            onChange={handleChange}
            className={`w-full border-2 ${
              errors.area ? "border-red-500" : "border-gray-400"
            } rounded p-2 focus:outline-none focus:border-blue-500`}
          />
        </div>

        {/* Here is the list of floor will show */}
        <div>
          <label className="block mb-1 font-medium">
            Floors <span className="text-red-500">*</span>
          </label>
          <select
            name="floor"
            value={formData.floor}
            onChange={handleChange}
            className={`w-full border-2 ${
              errors.floor ? "border-red-500" : "border-gray-400"
            } rounded p-2 focus:outline-none focus:border-blue-500`}
          >
            <option value="">Select Floor Number</option>
            {formData.premisesId && (
              <>
                {creatingListOfFloors.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </>
            )}
          </select>
          {errors.person_responsibility && (
            <p className="text-red-500 text-sm mt-1">
              {errors.person_responsibility}
            </p>
          )}
        </div>

        {/* Users to notify */}
        <div className="col-span-3 mt-4">
          <label className="block mb-1 font-medium">
            Select Users to Notify
          </label>
          <MultiSelectDropdown
            options={listOfUsersFromPremisesId
              .filter((user) => {
                const userId =
                  typeof user._id === "object" && "$oid" in user._id
                    ? user._id.$oid
                    : String(user._id);
                return userId !== formData.person_responsibility;
              })
              .map((user) => ({
                label: `${user.firstName} ${user.lastName}`,
                value:
                  typeof user._id === "object" && "$oid" in user._id
                    ? user._id.$oid
                    : String(user._id),
              }))}
            selectedValues={formData.users || []}
            onChange={handleUserSelectChange}
            placeholder="Select users..."
          />
          {formData.users && listOfUsersFromPremisesId.length > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              {formData.users.length} of{" "}
              {Math.max(0, listOfUsersFromPremisesId.length - 1)} users selected
            </p>
          )}
        </div>

        {formData.users && formData.users.length > 0 && (
          <div className="col-span-3 mt-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                setShowEmailModal(true);
              }}
              className="bg-[#0fa9a6] text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Notify Selected Users
            </button>
          </div>
        )}
      </div>

      {showEmailModal && (
        <Modal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
          }}
          modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg p-6"
        >
          <SendEmailNotificationToUsers
            selectedUsers={formData.report_send_email || []}
            fireDrillData={{
              premise: formData.premise,
              date_of_test_drills: formData.date_of_test_drills,
              person_responsibility: formData.person_responsibility,
              region: formData.region,
              area: formData.area,
              floor: formData.floor,
              next_drill_scheduled: formData.next_drill_scheduled,
            }}
            onClose={() => {
              setShowEmailModal(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};
